import type PickupLocation from '~/types/PickupLocation';

import { ActionKeys } from '~/actions/constants';
import { type ActionTypes } from '~/actions/index';

export type PickupsState = {
  readonly buildingLocationTypes: PickupLocation[];
  readonly carrier: Maybe<string>;
  readonly carrierAccountId: Maybe<string>;
  readonly confirmationCode: Maybe<string>;
  readonly isLoading: boolean;
  readonly pickupDate: Maybe<string>;
  readonly pickupPending: boolean;
  readonly pickupReadyByTime: Maybe<string>;
  readonly pickupSuccess: boolean;
  readonly pickupsLocation: Maybe<PickupLocation>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly serviceLevelInfo: any;
  readonly timeZone: Maybe<string>;
  readonly totalTransactions: number;
  readonly totalWeight: number;
  readonly transactionObjectIds: string[];
};

const initialState: PickupsState = {
  buildingLocationTypes: [],
  carrier: null,
  carrierAccountId: null,
  confirmationCode: null,
  isLoading: true,
  pickupDate: null,
  pickupPending: false,
  pickupReadyByTime: null,
  pickupSuccess: false,
  pickupsLocation: null,
  serviceLevelInfo: {},
  timeZone: null,
  totalTransactions: 0,
  totalWeight: 0,
  transactionObjectIds: [],
};

const pickupsReducer = (state: PickupsState = initialState, action: ActionTypes): PickupsState => {
  switch (action.type) {
    case ActionKeys.PICKUPS_FETCH_TRANSACTIONS:
      // Show the loader and clear the previous count while the new carrier's
      // transactions are fetched. Otherwise a stale `totalTransactions === 0`
      // makes MainContent redirect to /shipments with the wrong carrier before
      // this fetch resolves.
      return {
        ...state,
        carrier: action.payload.carrier,
        isLoading: true,
        totalTransactions: 0,
      };
    case ActionKeys.PICKUPS_FETCH_TRANSACTIONS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        serviceLevelInfo: action.payload.serviceLevelInfo,
        totalTransactions: action.payload.totalTransactions,
        totalWeight: action.payload.totalWeight,
        transactionObjectIds: action.payload.transactionObjectIds,
      };
    case ActionKeys.PICKUPS_FETCH_TRANSACTIONS_ERROR:
      return {
        ...state,
        isLoading: false,
      };
    case ActionKeys.PICKUPS_FETCH_CARRIER_ACCOUNT_ID_SUCCESS:
      return {
        ...state,
        carrierAccountId: action.payload.carrierAccountId,
      };
    case ActionKeys.PICKUPS_FETCH_CARRIER_BUILDING_LOCATION_TYPES_SUCCESS:
      return {
        ...state,
        buildingLocationTypes: action.payload.map(
          (val) => ({ name: val.name, value: val.name }), // Yes, name in both cases
        ),
      };
    case ActionKeys.PICKUPS_SEND_PICKUPS_REQUEST:
      return {
        ...state,
        pickupPending: true,
      };
    case ActionKeys.PICKUPS_SEND_PICKUPS_REQUEST_SUCCESS:
      return {
        ...state,
        confirmationCode: action.payload.confirmationCode,
        pickupDate: action.payload.pickupDate,
        pickupPending: false,
        pickupReadyByTime: action.payload.pickupReadyByTime,
        pickupSuccess: true,
        timeZone: action.payload.timeZone,
      };
    case ActionKeys.PICKUPS_SEND_PICKUPS_REQUEST_ERROR:
      return {
        ...state,
        pickupPending: false,
        pickupSuccess: false,
      };
    case ActionKeys.PICKUPS_RESET:
      // Called on unmount. Reset the carrier/count/loading state too so the next
      // pickup visit starts clean: without this, leftover `totalTransactions === 0`
      // and a stale `carrier` make MainContent redirect to /shipments (with the
      // wrong carrier) before the next fetch runs.
      return {
        ...state,
        carrier: null,
        confirmationCode: null,
        isLoading: true,
        pickupDate: null,
        pickupPending: false,
        pickupReadyByTime: null,
        pickupSuccess: false,
        timeZone: null,
        totalTransactions: 0,
      };
    default:
      return state;
  }
};

export default pickupsReducer;
